import { Button, Group, Title, Tooltip } from "@mantine/core";
import { IconMailFast } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { SplitButton } from "shared/components/global/SplitButton";
import {
    ComposeFormProvider,
    composeFormStateSchema,
    composeFormStateToDraftInfoInput,
    createComposeFormState,
    useComposeForm,
} from "shared/components/message/compose/context";
import { useComposeFormPermissionChecker } from "shared/components/message/compose/useComposeFormPermissionChecker";
import { ComposeForm } from "shared/components/message/ComposeForm";
import { MessageStatusBadge } from "shared/components/message/MessageStatusBadge";
import { useComposeFormActions } from "shared/components/message/useComposeFormActions";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { useDirtyFormBlocker } from "shared/hooks/useDirtyFormBlocker";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { fail, voiding } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { MessageStatus } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const Component = () => {
    const nav = useNavigate();
    const { id } = useParams({ from: "/_auth/messages/drafts/$id" });
    const {
        data: { message },
    } = useSuspenseQuery(query(id));

    const saveDraft = useGraphqlMutation(mutations.saveDraft);

    function handleSaveDraft() {
        const validation = form.validate();
        if (validation.hasErrors) {
            logger.warn(validation.errors);
            return;
        }

        saveDraft
            .mutateAsync({
                id,
                params: composeFormStateToDraftInfoInput.parse(form.getValues()),
            })
            .then((it) => qk.invalidate("messages", "composer", "message", it.updateDraft.id).then(() => it))
            .then(({ updateDraft: { id, subject } }) => {
                form.resetDirty();
                window.interop?.draftDidUpdate?.(id);
                notify.show.success({ message: `Saved draft '${subject}'.` });
            })
            .catch(notify.catch);
    }

    function handleSend() {
        if (form.validate().hasErrors) return;
        const params = composeFormStateToDraftInfoInput.parse(form.getValues());

        openSendModal({
            async getMessage() {
                const { updateDraft } = await saveDraft.mutateAsync({ id, params });
                return updateDraft;
            },
        });
    }

    const form = useComposeForm({
        initialValues: createComposeFormState(message),
        validate: zodResolver(composeFormStateSchema),
    });

    const { openSendModal } = useComposeFormActions(form);
    const actions = useMessageActions();
    const permissionChecker = useComposeFormPermissionChecker(form);

    useDirtyFormBlocker(form);

    return (
        <Content paper>
            <Content.Heading backable>
                <Title order={3}>Edit Message</Title>
                <MessageStatusBadge status={MessageStatus.Draft} />
            </Content.Heading>

            <ComposeFormProvider form={form}>
                <ComposeForm />
            </ComposeFormProvider>

            <Tooltip {...permissionChecker.getTooltipProps()}>
                <Group justify="flex-end" wrap="nowrap">
                    <Button
                        variant="default"
                        onClick={handleSaveDraft}
                        disabled={permissionChecker.hasErrors}
                        loading={saveDraft.isPending}
                    >
                        Save
                    </Button>

                    <SplitButton
                        onClick={voiding(handleSend)}
                        menu={{
                            "Send Preview": { fn: () => actions.openPreviewModal({ form }) },
                            "Schedule for Later": {
                                fn: async () => {
                                    if (form.validate().hasErrors) return;
                                    await actions.openScheduleDeliveryModal({
                                        isDisabled: () =>
                                            Object.values(form.getValues().audience).every((part) => part.length == 0)
                                                ? "You must specify an audience."
                                                : null,
                                        async getMessage() {
                                            if (form.validate().hasErrors) return fail("Form has errors.");

                                            const { updateDraft } = await saveDraft.mutateAsync({
                                                id,
                                                params: composeFormStateToDraftInfoInput.parse(form.getValues()),
                                            });

                                            return updateDraft;
                                        },
                                    });
                                    await nav({ to: "/messages/scheduled" });
                                },
                            },
                        }}
                        disabled={permissionChecker.hasErrors}
                        leftSection={<IconMailFast size={16} />}
                    >
                        Review & Send
                    </SplitButton>
                </Group>
            </Tooltip>
        </Content>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: qk("messages", "composer", "message", id),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query ComposeMessage($id: UUID!) {
                        message(id: $id) {
                            id
                            ...ComposeDetails
                        }
                    }
                `),
                { id },
            ),
        gcTime: 0,
    });

const mutations = {
    saveDraft: graphql(`
        mutation DraftsSave($id: UUID!, $params: DraftInfoInput!) {
            updateDraft(id: $id, params: $params) {
                id
                subject
            }
        }
    `),
    sendMessage: graphql(`
        mutation DraftsSendMesage($id: UUID!, $expireAt: Instant) {
            sendMessage(id: $id, expireAt: $expireAt) {
                id
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/messages/drafts/$id")({
    loader: ({ params: { id } }) => queryClient.ensureQueryData(query(id)),
    component: Component,
});
